import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Truck, ShieldCheck } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { WishButton } from "@/components/WishButton";
import { ShareButton } from "@/components/ShareButton";
import { Stars, RatingSummary, SavingsTag } from "@/components/Stars";

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();

  useEffect(() => { api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [id]);
  useEffect(() => {
    if (!p) return;
    document.title = `${p.title} – ${new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(p.price)} | Sconto.Market`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", p.description.slice(0, 155));
    return () => { document.title = "Sconto.Market – Offerte del giorno con spedizione gratuita"; };
  }, [p]);

  if (err) return <p className="p-10" data-testid="product-not-found">Prodotto non trovato.</p>;
  if (!p) return <p className="p-10 tag">Caricamento...</p>;

  return (
    <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10" data-testid="product-page">
      <Link to="/" className="tag inline-flex items-center gap-2 mb-8 hover:text-[#FF3333]" data-testid="back-to-shop">
        <ArrowLeft size={14} /> Torna alle offerte
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-12 border border-[#0F0F0F] bg-white">
        <div className="md:col-span-7 md:border-r border-b md:border-b-0 border-[#0F0F0F] relative">
          <img src={p.image} alt={p.title} className="w-full max-h-[80vh] aspect-square object-cover" />
          {p.discount > 0 && <span className="absolute top-4 left-4 bg-[#FF3333] text-white tag px-3 py-1.5">Sconto -{p.discount}%</span>}
          <WishButton id={p.id} className="absolute top-4 right-4" />
        </div>
        <div className="md:col-span-5 p-6 md:p-10 flex flex-col gap-6">
          <span className="tag text-[#555]">{p.category}</span>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight" data-testid="product-title">{p.title}</h1>
          <RatingSummary p={p} />
          <div>
            {p.list_price && <p className="font-mono text-sm text-[#555] line-through">{eur(p.list_price)}</p>}
            <p className="font-display font-black text-5xl tracking-tight" data-testid="product-detail-price">{eur(p.price)}</p>
            <div className="mt-2"><SavingsTag p={p} big /></div>
            <p className="text-xs text-[#555] mt-1">IVA inclusa · Spedizione gratuita</p>
          </div>
          <p className="text-[#333] leading-relaxed" data-testid="product-description">{p.description}</p>
          <div className="flex gap-3">
            <select data-testid="product-qty-select" value={qty} onChange={(e) => setQty(+e.target.value)} className="field w-24 font-mono">
              {Array.from({ length: 10 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
            </select>
            <button
              data-testid="product-add-to-cart"
              onClick={() => { add(p, qty); toast.success("Aggiunto al carrello"); setOpen(true); }}
              className="btn-primary flex-1 h-12 font-medium"
            >
              Aggiungi al carrello
            </button>
          </div>
          <div className="border-t border-[#0F0F0F] pt-6 space-y-3 text-sm">
            <ShareButton path={`/prodotto/${p.id}`} text={`${p.title} a soli ${eur(p.price)} su Sconto.Market`} label="Condividi questo prodotto" testid="product-share-button" className="w-fit" />
            <p className="flex items-center gap-3"><Truck size={18} strokeWidth={1.5} /> Consegna diretta a domicilio in 2-5 giorni</p>
            <p className="flex items-center gap-3"><ShieldCheck size={18} strokeWidth={1.5} /> Pagamento sicuro e reso entro 30 giorni</p>
          </div>
        </div>
      </div>
      <Reviews pid={p.id} />
    </main>
  );
}

const Reviews = ({ pid }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`/products/${pid}/reviews`).then((r) => setList(r.data)); }, [pid]);
  return (
    <section className="mt-12" data-testid="product-reviews">
      <span className="tag text-[#555]">Opinioni verificate</span>
      <h2 className="font-display font-extrabold text-3xl tracking-tight mt-1 mb-6">Recensioni dei clienti</h2>
      {list.length === 0 ? (
        <p className="text-[#555]" data-testid="no-reviews">Ancora nessuna recensione. Le recensioni arrivano solo da clienti che hanno ricevuto il prodotto.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((r) => (
            <div key={r.id} className="border border-[#0F0F0F] bg-white p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <Stars value={r.rating} />
                <span className="font-mono text-xs text-[#555]">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed">{r.comment}</p>
              {r.photo_path && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_path}`} target="_blank" rel="noreferrer">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo_path}`} alt="Foto del cliente" data-testid={`review-photo-${r.id}`}
                    className="mt-3 w-32 h-32 object-cover border border-[#0F0F0F] hover:opacity-80 transition-opacity" loading="lazy" />
                </a>
              )}
              <p className="mt-3 tag text-[#555]">{r.author} · Acquisto verificato</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
